import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { vehicleAPI } from '../../services/api';
import { Container, Spinner } from 'react-bootstrap';
import SaudiLicensePlate from '../../components/SaudiLicensePlate';
import { FiFileText, FiShield, FiAlertTriangle } from 'react-icons/fi';

interface VehicleData {
  plateNumber: string;
  vehicleMake?: string;
  vehicleModel?: string;
  color?: string;
  licenseImageUrl?: string;
  insuranceImageUrl?: string;
  companyInfo?: {
    companyName: string;
    websiteUrl?: string;
    logoUrl?: string;
    phone?: string;
    email?: string;
  };
}

const VehiclePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const plateNumber = searchParams.get('plate');

  const [vehicle, setVehicle] = useState<VehicleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    const fetchVehicle = async () => {
      if (!plateNumber) {
        setError('No plate number provided');
        setLoading(false);
        return;
      }
      try {
        const response = await vehicleAPI.getVehiclePublic(plateNumber);
        setVehicle(response.data);
      } catch (err: any) {
        setError(err.response?.status === 404 ? 'Vehicle not found' : 'Failed to load vehicle information');
      } finally {
        setLoading(false);
      }
    };
    fetchVehicle();
  }, [plateNumber]);

  if (loading) {
    return (
      <div className="public-shell d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="public-shell d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{
            width: 72, height: 72, background: '#fef2f2', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1.25rem', fontSize: '1.75rem', color: '#dc2626',
          }}>
            <FiAlertTriangle />
          </div>
          <h3 style={{ fontWeight: 700, color: '#0f172a' }}>Vehicle Not Found</h3>
          <p style={{ color: '#64748b' }}>{error || 'No vehicle information available.'}</p>
        </div>
      </div>
    );
  }

  const infoItems = [
    vehicle.vehicleMake  && { label: 'Make',  value: vehicle.vehicleMake  },
    vehicle.vehicleModel && { label: 'Model', value: vehicle.vehicleModel },
    vehicle.color        && { label: 'Color', value: vehicle.color        },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <div className="public-shell">

      {/* ── Hero ── */}
      <div className="public-hero">
        <Container>
          {vehicle.companyInfo?.logoUrl && (
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <img
                src={vehicle.companyInfo.logoUrl}
                alt={vehicle.companyInfo.companyName}
                style={{ maxHeight: 64, maxWidth: 200, objectFit: 'contain' }}
              />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <SaudiLicensePlate plateNumber={vehicle.plateNumber} />
          </div>

          {/* ── Hero extra slot — add content here in future ── */}
          {/* <div className="public-hero-extra"> ... </div> */}
        </Container>
      </div>

      {/* ── Content ── */}
      <div className="public-content">
        <Container style={{ maxWidth: 1100 }}>

          {/* Vehicle info chips */}
          {infoItems.length > 0 && (
            <div
              className="section-card mb-4"
              style={{ background: '#fff', borderRadius: 'var(--r)', boxShadow: 'var(--shadow-md)', padding: '1.25rem' }}
            >
              <div style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: '0.75rem' }}>
                Vehicle Details
              </div>
              <div className="info-grid">
                {infoItems.map(({ label, value }) => (
                  <div className="info-item" key={label}>
                    <div className="info-item-label">{label}</div>
                    <div className="info-item-value">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Documents: side by side ── */}
          <div className="docs-grid">

            {/* License PDF */}
            <div className="doc-card">
              <div className="doc-card-header doc-card-header-license">
                <FiFileText /> Vehicle License
              </div>
              {vehicle.licenseImageUrl ? (
                <>
                  <iframe
                    src={`${vehicle.licenseImageUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                    title="Vehicle License"
                    width="100%"
                    className="pdf-viewer"
                  />
                  <div className="text-center py-2" style={{ borderTop: '1px solid #dcfce7' }}>
                    <a href={vehicle.licenseImageUrl} target="_blank" rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-success">
                      Open License PDF
                    </a>
                  </div>
                </>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                  <FiFileText size={28} style={{ opacity: 0.35, display: 'block', margin: '0 auto 0.5rem' }} />
                  No license document available
                </div>
              )}
            </div>

            {/* Insurance PDF */}
            <div className="doc-card">
              <div className="doc-card-header doc-card-header-insurance">
                <FiShield /> Vehicle Insurance
              </div>
              {vehicle.insuranceImageUrl ? (
                <>
                  <iframe
                    src={`${vehicle.insuranceImageUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                    title="Vehicle Insurance"
                    width="100%"
                    className="pdf-viewer"
                  />
                  <div className="text-center py-2" style={{ borderTop: '1px solid #dbeafe' }}>
                    <a href={vehicle.insuranceImageUrl} target="_blank" rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary">
                      Open Insurance PDF
                    </a>
                  </div>
                </>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                  <FiShield size={28} style={{ opacity: 0.35, display: 'block', margin: '0 auto 0.5rem' }} />
                  No insurance document available
                </div>
              )}
            </div>

          </div>{/* end docs-grid */}

        </Container>
      </div>
    </div>
  );
};

export default VehiclePage;
